import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Users,
  GraduationCap,
  Briefcase,
  MapPin,
  Clock,
  ArrowRight,
  CheckCircle,
  Send
} from 'lucide-react';
import { careers, companyInfo } from '../data/mock';
import SEO from '../components/SEO';

const CareersPage = () => {
  return (
    <div className="bg-slate-950 pt-20">
      <SEO 
        title="Careers"
        description="Join CORE IT Group team. We value curiosity, accountability, and continuous learning. Explore cybersecurity career opportunities."
        keywords="cybersecurity jobs, IT security careers, GRC consultant jobs, vendor risk analyst, cybersecurity analyst positions"
        canonical="/careers"
      />
      {/* Hero Section */}
      <section className="relative py-24">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-1/3 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-cyan-400 font-medium text-sm tracking-wider uppercase">Careers</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mt-4 mb-6">
              Join Our Team
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed">
              {careers.intro}
            </p>
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-24 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-cyan-400 font-medium text-sm tracking-wider uppercase">Benefits</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mt-4 mb-6">Why Work With Us</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {careers.benefits.map((benefit, index) => {
              const icons = [Users, GraduationCap, Briefcase];
              const IconComponent = icons[index];
              return (
                <Card
                  key={index}
                  className="bg-slate-900 border-slate-800 hover:border-cyan-500/50 transition-all duration-300 group"
                >
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-cyan-500/20 to-teal-500/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="w-8 h-8 text-cyan-400" />
                    </div>
                    <p className="text-white font-medium text-lg">{benefit}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-cyan-400 font-medium text-sm tracking-wider uppercase">Open Positions</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mt-4 mb-6">Current Opportunities</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              We're always looking for talented individuals who share our passion for cybersecurity.
            </p>
          </div>

          <div className="space-y-6 max-w-4xl mx-auto">
            {careers.openings.map((job) => (
              <Card
                key={job.id}
                className="bg-slate-900/50 border-slate-800 hover:border-cyan-500/50 transition-all duration-300 group overflow-hidden"
              >
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row md:items-center justify-between p-6 gap-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-white group-hover:text-cyan-400 transition-colors mb-2">
                        {job.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {job.type}
                        </div>
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {job.location}
                        </div>
                      </div>
                    </div>
                    <a href={`mailto:${companyInfo.careersEmail}?subject=Application for ${job.title}`}>
                      <Button className="bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-300 group/btn">
                        Apply Now
                        <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Apply CTA */}
      <section className="py-24 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-slate-900 border-slate-800 overflow-hidden">
            <CardContent className="p-8 sm:p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-xl flex items-center justify-center">
                <Send className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                Ready to Apply?
              </h2>
              <p className="text-slate-400 mb-8 max-w-lg mx-auto">
                Send your resume to our careers team and let's start a conversation about your future at CORE IT Group.
              </p>
              <a href={`mailto:${companyInfo.careersEmail}`}>
                <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-300">
                  {companyInfo.careersEmail}
                </Button>
              </a>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
